import { createContext, useContext, useState } from 'react';
import { IntlProvider } from 'react-intl';

import flattenMessages from '@/lib/translations/flattenMessages';
import { enUSMessages } from '@/lib/translations/locales/en-US';
// import { frFRMessages } from '@/lib/translations/locales/fr-FR'; <- Example of how to add a new language. File exists but is empty
import { type TranslationsSchema } from '@/lib/translations/types';

const LOCALE_LOCAL_STORAGE_KEY = 'anyshift-locale';

const getLocaleFromLocalStorage = (): AvailableLocales => {
  const locale = localStorage.getItem(
    LOCALE_LOCAL_STORAGE_KEY,
  ) as AvailableLocales | null;

  if (locale === null || !Object.values(AvailableLocales).includes(locale)) {
    return AvailableLocales.EnUS;
  }

  return locale;
};

const AvailableLocales = {
  EnUS: 'en-US',
  // FrFR: 'fr-FR', <- Example of how to add a new language
} as const;
type AvailableLocales =
  (typeof AvailableLocales)[keyof typeof AvailableLocales];

const allMessages = {
  [AvailableLocales.EnUS]: enUSMessages,
  // [AvailableLocales.FrFR]: frFRMessages, <- Example of how to add a new language
} satisfies Record<AvailableLocales, TranslationsSchema>;

type TranslationsContextValue = {
  locale: AvailableLocales;
  setLocale: (locale: AvailableLocales) => void;
};

export const TranslationsContext =
  createContext<TranslationsContextValue | null>(null);

export const TranslationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [locale, setLocale] = useState<AvailableLocales>(
    getLocaleFromLocalStorage(),
  );
  const intlMessages = allMessages[locale];

  return (
    <TranslationsContext.Provider
      value={{
        locale,
        setLocale,
      }}
    >
      <IntlProvider locale={locale} messages={flattenMessages(intlMessages)}>
        {children}
      </IntlProvider>
    </TranslationsContext.Provider>
  );
};

export const useLocale = (): TranslationsContextValue => {
  const context = useContext(TranslationsContext);
  if (context === null) {
    throw new Error('useSetLocale must be used within a TranslationsProvider');
  }

  return {
    locale: context.locale,
    setLocale: (locale: AvailableLocales) => {
      localStorage.setItem(LOCALE_LOCAL_STORAGE_KEY, locale);
      context.setLocale(locale);
    },
  };
};
